import React, { useState } from 'react'

import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { format as formatDate } from 'date-fns'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'

import { setTitleOrderModalOpen } from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { InputField, NumberField, SelectField } from '@/components/FormikInputs'
import LockChild from '@/components/LockChild/LockChild'
import { ssnFormatedRegex } from '@/constants/globals'
import { maritalStatusOptions } from '@/features/application/applicationSlice'
import { useCurrentApplication } from '@/graphql/services/application'
import { useGetSsnUnmaskedQuery } from '@/services/applicationProfile'
import { useCreateTitleOrderMutation, useGetTitleOrderQuery } from '@/services/titleOrder'
import { useGetLoggedUserQuery } from '@/services/user'
import { getApplicationProfileByPosition } from '@/utils/applicationProfile'

const whenValidatorObject = (errorMessage, value: any = false) => ({
  is: value,
  then: Yup.string().nullable().required(errorMessage),
})

const schema = Yup.object().shape({
  property_street_name: Yup.string().required('The street is required'),
  property_unit_number: Yup.string().required('The number is required'),
  property_city: Yup.string().required('The city is required'),
  property_state: Yup.string().required('The state is required'),
  property_county: Yup.string().required('The county is required'),
  property_zip: Yup.string().required('The zip Code is required'),
  primary_contact: Yup.string().required('The primary contact is required'),
  email: Yup.string().email('A valid email is required').required('A valid email is required'),
  estimated_settlement_date: Yup.date().required('The estimated settlement date is required'),
  new_loan_amount: Yup.string().required('The new loan amount is required'),
  new_loan_number: Yup.string().required('The new loan number is required'),
  first_name: Yup.string().required('The first name is required'),
  middle_name: Yup.string().nullable(),
  last_name: Yup.string().required('The last name is required'),
  customer_email: Yup.string().nullable().email('A valid email is required'),
  customer_phone: Yup.string().nullable(),
  marital_status: Yup.string().nullable(),
  ssn: Yup.string()
    .nullable()
    .matches(ssnFormatedRegex, 'Please enter a valid SSN (AAA-GG-SSSS)')
    .required('The ssn is required'),
  same_address_as_property_address: Yup.boolean(),
  spouse_first_name: Yup.string().nullable(),
  spouse_last_name: Yup.string().nullable(),
  spouse_email: Yup.string().nullable().email('A valid email is required'),
  spouse_phone: Yup.string().nullable(),
  spouse_ssn: Yup.string()
    .nullable()
    .matches(ssnFormatedRegex, 'Please enter a valid SSN (AAA-GG-SSSS)'),
})

export const TitleOrderModal = () => {
  const [order, setOrder] = useState(null)
  const dispatch = useAppDispatch()
  const orderModelOpen = useAppSelector((state) => state.general.titleOrderModalOpen)

  // If OrderViewOnly is a number, we are trying to show a form that was ordered
  const orderViewOnly = typeof orderModelOpen === 'number'
  const idOrder: number = orderViewOnly ? orderModelOpen : null
  const { data: titleOrders } = useGetTitleOrderQuery(
    { id: idOrder },
    {
      skip: !orderViewOnly,
    }
  )

  const { data: user } = useGetLoggedUserQuery()
  const { data: application } = useCurrentApplication()
  const [createTitleOrder, { isError, error }] = useCreateTitleOrderMutation()

  const applicantProfile = getApplicationProfileByPosition(application?.uuid, 1)?.profile
  const spouseProfile = getApplicationProfileByPosition(application?.uuid, 2)?.profile

  const { data: applicantSSN } = useGetSsnUnmaskedQuery(applicantProfile?.uuid, {
    skip: !applicantProfile || !orderModelOpen,
  })
  const { data: spouseSSN } = useGetSsnUnmaskedQuery(spouseProfile?.uuid, {
    skip: !spouseProfile || !orderModelOpen,
  })

  if (!application || !applicantProfile) return null

  const { address } = application.property
  const { street, county, city, state, zipCode, unitNumber = '' } = address

  const {
    first_name,
    middle_name = '',
    last_name,
    marital_status = '',
    email = null,
    phone = null,
  } = applicantProfile

  const getTitleOrderErrors = (error) => {
    return error.data?.errors || []
  }

  const number_regex = new RegExp(/^([0-9]*)/)

  const formatStreetName = () => {
    let street_name = street.replace(number_regex, '').trimStart()

    if (unitNumber) {
      street_name += ` Unit ${unitNumber}`
    }

    return street_name
  }

  let initialValues

  if (orderViewOnly && titleOrders?.count) {
    initialValues = titleOrders.results[0]
  } else {
    initialValues = {
      property_street_name: formatStreetName(),
      property_county: county,
      property_city: city,
      property_state: state,
      property_zip: zipCode,
      property_unit_number: number_regex.exec(street || '')[0],
      primary_contact: user?.data?.full_name || '',
      email: user?.username || '',
      estimated_settlement_date: application.estimatedCloseOn,
      new_loan_amount: application.requestedInvestment,
      new_loan_number: application.externalId || '',
      marital_status,
      first_name,
      middle_name,
      last_name,
      customer_email: email,
      customer_phone: phone,
      ssn: applicantSSN?.social_security_number,
      spouse_first_name: spouseProfile?.first_name || null,
      spouse_middle_name: spouseProfile?.middle_name || null,
      spouse_last_name: spouseProfile?.last_name || null,
      spouse_ssn: spouseSSN?.social_security_number || null,
      spouse_email: spouseProfile?.email || null,
      spouse_phone: spouseProfile?.phone || null,
      same_address_as_property_address: true,
      verification: false,
    }
  }

  const handleSameAddress = (sameAddressFlag, values, setFieldValue) => {
    if (sameAddressFlag) {
      setFieldValue('same_address_as_property_address', sameAddressFlag)

      return
    }

    setFieldValue('same_address_as_property_address', sameAddressFlag)
  }

  return orderModelOpen ? (
    <div
      id='overlay'
      className='top-0 left-0 fixed w-screen h-screen bg-gray-400/60 z-50 flex items-center justify-center'
    >
      <div className='w-[48%] max-h-[90%] bg-white p-8 mb-4 overflow-y-auto'>
        <div className='flex justify-between mb-4'>
          <h2 className='text-sky-400'>Title Report - Order Form</h2>
          <XMarkIcon
            className='cursor-pointer'
            width={30}
            fill='grey'
            onClick={() => dispatch(setTitleOrderModalOpen(false))}
          />
        </div>

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={async (values: any, actions) => {
            const newValues = {
              ...values,
              application: application.id,
            }

            delete newValues.verification

            const data: any = await createTitleOrder(newValues)

            if (!data.error) {
              setOrder(data.data)
              actions.setSubmitting(false)
            }
          }}
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <>
              {values.accepted === false && (
                <p className='mb-5 text-error text-lg'>
                  This order was not sent to CET. Please, place a new order
                </p>
              )}
              <LockChild shouldBeLocked={orderViewOnly}>
                <Form>
                  <h3 className='mb-4'>Property Details</h3>

                  <div className='grid grid-cols-4 gap-4 mb-8'>
                    <InputField
                      label='Street Name'
                      style='default'
                      name='property_street_name'
                      placeholder='Street Name'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='Number'
                      style='default'
                      name='property_unit_number'
                      placeholder='Number'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='ZIP'
                      style='default'
                      name='property_zip'
                      placeholder='ZIP'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='City'
                      style='default'
                      name='property_city'
                      placeholder='City'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='State'
                      style='default'
                      name='property_state'
                      placeholder='State'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='County'
                      style='default'
                      name='property_county'
                      placeholder='County'
                      type='text'
                      showError={true}
                    />
                  </div>

                  <h3 className='mb-4'>Order Details</h3>

                  <div className='grid grid-cols-4 gap-4 mb-8'>
                    <InputField
                      label='Primary Contact'
                      style='default'
                      name='primary_contact'
                      placeholder='Primary Contact'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='Est. Settlement Date'
                      style='default'
                      name='estimated_settlement_date'
                      placeholder='Est. Settlement Date'
                      type='date'
                      showError={true}
                    />
                    <NumberField
                      label='New Loan Amount'
                      inputStyle='default'
                      name='new_loan_amount'
                      placeholder='New Loan Amount'
                      suffix=''
                      prefix='$'
                      thousandSeparator={true}
                      showError={true}
                    />
                    <InputField
                      label='New Loan No.'
                      style='default'
                      name='new_loan_number'
                      placeholder='New Loan No.'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='Email'
                      style='default'
                      name='email'
                      placeholder='Email'
                      type='text'
                      showError={true}
                    />
                  </div>

                  <h3 className='mb-4'>Customer</h3>

                  <div className='grid grid-cols-4 gap-4 mb-4'>
                    <InputField
                      label='First Name'
                      style='default'
                      name='first_name'
                      placeholder='First Name'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='Middle Name'
                      style='default'
                      name='middle_name'
                      placeholder='Middle Name'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='Last Name'
                      style='default'
                      name='last_name'
                      placeholder='Last Name'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='SSN'
                      style='default'
                      name='ssn'
                      placeholder='SSN'
                      type='text'
                      showError={true}
                    />
                  </div>

                  <div className='grid grid-cols-4 gap-4 mb-8'>
                    <InputField
                      label='Email'
                      style='default'
                      name='customer_email'
                      placeholder='Email'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='Phone'
                      style='default'
                      name='customer_phone'
                      placeholder='Phone'
                      type='text'
                      showError={true}
                    />
                    <SelectField
                      extraClasses='col-span-2'
                      label='Marital Status'
                      style='default'
                      name='marital_status'
                      options={maritalStatusOptions}
                    />
                  </div>

                  <h3 className='mb-4'>Co-Borrower</h3>

                  <div className='grid grid-cols-4 gap-4 mb-8'>
                    <InputField
                      label='First Name'
                      style='default'
                      name='spouse_first_name'
                      placeholder='First Name'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='Middle Name'
                      style='default'
                      name='spouse_middle_name'
                      placeholder='Middle Name'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='Last Name'
                      style='default'
                      name='spouse_last_name'
                      placeholder='Last Name'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='SSN'
                      style='default'
                      name='spouse_ssn'
                      placeholder='SSN'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='Email'
                      style='default'
                      name='spouse_email'
                      placeholder='Email'
                      type='text'
                      showError={true}
                    />
                    <InputField
                      label='Phone'
                      style='default'
                      name='spouse_phone'
                      placeholder='Phone'
                      type='text'
                      showError={true}
                    />
                  </div>

                  <div className='flex justify-between mb-4'>
                    <h3>Address</h3>
                    <div className='flex gap-4 items-center'>
                      <p>Use same address as property</p>
                      <Field
                        name='same_address_as_property_address'
                        type='checkbox'
                        onChange={(e) => {
                          const value = !(e.target.value == 'true')
                          handleSameAddress(value, values, setFieldValue)
                        }}
                      />
                    </div>
                  </div>

                  {isError && error && (
                    <div className='mb-4 flex flex-col gap-2 text-error'>
                      {getTitleOrderErrors(error).map((err, index) => (
                        <p
                          key={index}
                          className='text-center'
                        >
                          {err}
                        </p>
                      ))}
                    </div>
                  )}
                  <div className='flex justify-center gap-8'>
                    <div className='flex gap-4 items-center'>
                      {order ? (
                        <div className='flex flex-col gap-2 items-center text-center'>
                          <div className='flex justify-center items-center'>
                            <p>Your Title Report has been submitted</p>
                            <CheckIcon
                              className='ml-5 text-white bg-success rounded'
                              width={20}
                            />
                          </div>
                          <p>
                            Order date {formatDate(new Date(order.created_at), 'dd/MM/yyyy h:mm a')}{' '}
                            | File Number:{' '}
                            {order?.['file_number'] == null ? '---' : order?.['file_number']}
                          </p>
                          <div
                            className='flex items-center text-center m-auto'
                            onClick={() => {
                              dispatch(setTitleOrderModalOpen(false))
                              setOrder(null)
                            }}
                          >
                            <span className='table m-auto text-blue-500 underline cursor-pointer'>
                              Close this dialogue
                            </span>
                          </div>
                        </div>
                      ) : (
                        !orderViewOnly && (
                          <>
                            <p className='text-right w-[300px]'>
                              I have verified the information on this form and it is ready for
                              submission
                            </p>
                            <Field
                              name='verification'
                              type='checkbox'
                            />
                            <button
                              type='submit'
                              className='px-4 py-2 text-lg bg-sky-400 disabled:bg-sky-300 text-white rounded-md'
                              disabled={!values.verification || isSubmitting}
                            >
                              {isSubmitting ? 'Submitting...' : isError ? 'Try again' : 'Order'}
                            </button>
                          </>
                        )
                      )}
                    </div>
                  </div>
                </Form>
              </LockChild>
            </>
          )}
        </Formik>
      </div>
    </div>
  ) : null
}
